import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useEscapeToClose } from "@/lib/a11y";
import {
  LayoutDashboard,
  Camera,
  Trophy,
  ListChecks,
  MoreHorizontal,
  Calendar,
  BarChart3,
  Sparkles,
  Shield,
  Settings as SettingsIcon,
  Server,
  Globe,
  LogOut,
} from "lucide-react";

// Mobile bottom nav (≤md). 5 primary tabs + an inline "More" sheet for
// the remaining role-gated screens. Desktop sidebar handles everything
// ≥xl (1280px), so this component is only rendered with `xl:hidden`.

const MOBILE_PRIMARY = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/photographers", label: "Photographers", icon: Camera },
  { to: "/competitions", label: "Comps", icon: Trophy },
  { to: "/results", label: "Results", icon: ListChecks },
];

export default function MobileBottomNav({ user, onLogout }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const isViewer = user?.role === "VIEWER";
  const isAdmin = user?.role === "ADMIN";
  const isSuper = user?.role === "SUPERADMIN";
  useEscapeToClose(moreOpen ? () => setMoreOpen(false) : null);

  // Items inside the More sheet — filtered by role, mirrors desktop sidebar.
  const moreItems = [
    !isViewer && { to: "/seasons", label: "Seasons", icon: Calendar },
    !isViewer && { to: "/stats", label: "Statistics", icon: BarChart3 },
    !isViewer && { to: "/awards", label: "Season Stats", icon: Sparkles },
    isAdmin && { to: "/users", label: "Users", icon: Shield },
    isAdmin && { to: "/settings", label: "Settings", icon: SettingsIcon },
    isSuper && { to: "/platform", label: "Platform", icon: Server },
  ].filter(Boolean);

  return (
    <>
      <nav
        className="xl:hidden fixed bottom-0 inset-x-0 z-50 flex border-t"
        style={{ background: "#0d0d0d", borderColor: "#1f1f1f" }}
        data-testid="mobile-bottom-nav"
      >
        {MOBILE_PRIMARY.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setMoreOpen(false)}
            className={({ isActive }) =>
              "flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] " +
              (isActive ? "text-amber-400" : "text-neutral-400")
            }
            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <item.icon size={20} strokeWidth={1.6} />
            <span className="uppercase tracking-wide">{item.label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          className={
            "flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] " +
            (moreOpen ? "text-amber-400" : "text-neutral-400")
          }
          data-testid="mobile-nav-more"
        >
          <MoreHorizontal size={20} strokeWidth={1.6} />
          <span className="uppercase tracking-wide">More</span>
        </button>
      </nav>

      {moreOpen && (
        <div
          className="xl:hidden fixed inset-0 z-[60] flex items-end"
          onClick={() => setMoreOpen(false)}
          data-testid="mobile-more-sheet-backdrop"
        >
          <div
            className="w-full rounded-t-2xl p-4 border-t"
            style={{ background: "#0d0d0d", borderColor: "#1f1f1f" }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-more-title"
            data-testid="mobile-more-sheet"
          >
            <div className="mx-auto h-1 w-10 rounded-full bg-neutral-700 mb-4" aria-hidden="true" />
            <div
              id="mobile-more-title"
              className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-3 px-1"
            >
              {user?.name} · {user?.role}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {moreItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMoreOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg bg-neutral-900 text-sm"
                  data-testid={`mobile-more-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <item.icon size={18} strokeWidth={1.6} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
              <a
                href="/portal"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 px-3 py-3 rounded-lg bg-neutral-900 text-sm"
                onClick={() => setMoreOpen(false)}
                data-testid="mobile-more-portal"
              >
                <Globe size={18} strokeWidth={1.6} />
                <span>Public portal</span>
              </a>
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(false);
                  onLogout();
                }}
                className="flex items-center gap-3 px-3 py-3 rounded-lg bg-neutral-900 text-sm text-left"
                data-testid="mobile-more-logout"
              >
                <LogOut size={18} strokeWidth={1.6} />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
